<template>
  <div class="billing_show">
    <page-header :title="$t('billingProfile.edit')">
      <template v-slot:actions>
        <router-link :to="{ name: 'billing.index', params: { clientId: $store.state.currentClient.uuid } }" class="btn btn-secondary btn-sm">
          <i class="fa fa-arrow-left mr-1"></i> Back
        </router-link>
        <button v-if="!loading && !error && activeTab === 'profile'" class="btn btn-success btn-sm ml-2" :disabled="saving" @click.prevent="save">
          <i v-if="saving" class="fa fa-spinner fa-spin mr-1"></i>
          <i v-else class="fa fa-save mr-1"></i>
          {{ $t('save') || 'Save' }}
        </button>
      </template>
    </page-header>
    <page-content>
      <ntm-block>
        <div v-if="loading" class="p-3">
          <i class="fa fa-spinner fa-spin mr-2"></i> Loading billing profile...
        </div>
        <div v-else>
          <div v-if="error" class="alert alert-danger m-3">{{ error }}</div>
          <div v-else>
            <ul class="nav nav-tabs nav-tabs-block pl-4" role="tablist">
              <li class="nav-item" role="presentation">
                <a class="nav-link" :class="{ active: activeTab === 'profile' }" href="#" role="tab" @click.prevent="setTab('profile')">
                  {{ $t('billingUsage.profileTab') }}
                </a>
              </li>
              <li class="nav-item" role="presentation">
                <a class="nav-link" :class="{ active: activeTab === 'usage' }" href="#" role="tab" @click.prevent="setTab('usage')">
                  {{ $t('billingUsage.tab') }}
                </a>
              </li>
            </ul>

            <div v-if="usageOpened" v-show="activeTab === 'usage'" class="p-3">
              <billing-usage-stats :billing-profile-id="model.id" :currency="model.currency" />
            </div>

            <div v-show="activeTab === 'profile'" class="p-3">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <div class="font-w600 h5 mb-1">{{ model.name || model.companyName || 'Billing Profile' }}</div>
                  <div class="text-muted small">ID: {{ model.id }}</div>
                </div>
                <div>
                  <span v-if="model.currency" class="badge badge-primary">{{ model.currency }}</span>
                </div>
              </div>

              <!-- Payment method card (hidden while card payments are off, see config/billing.js) -->
              <div v-if="cardPaymentsEnabled && (model.pmType || model.pmLastFour)" class="rounded bg-body-light mb-4">
                <div class="p-2 d-flex align-items-center">
                  <div class="ml-2 mr-3">
                    <i class="fa fa-credit-card fa-2x text-muted"></i>
                  </div>
                  <div class="flex-grow-1">
                    <div class="font-w600">Payment Method</div>
                    <div class="text-muted">{{ formattedPayment }}</div>
                  </div>
                </div>
              </div>

              <!-- Add/Change card (hidden while card payments are off, see config/billing.js) -->
              <div v-if="cardPaymentsEnabled" class="mb-4" data-testid="billing-add-card">
                <div class="">
                  <div v-if="!addingCard">
                    <button class="btn btn-outline-primary btn-sm" @click.prevent="startAddCard">
                      <i class="fa fa-credit-card mr-1"></i>
                      {{ model.pmLastFour ? 'Change card' : 'Add card' }}
                    </button>
                    <div v-if="stripeInitError" class="text-danger mt-2">{{ stripeInitError }}</div>
                  </div>
                  <div v-else>
                    <div v-if="cardError" class="alert alert-danger py-2">{{ cardError }}</div>
                    <div ref="cardElement" class="stripe-card-element form-control p-2"></div>
                    <div class="small text-muted mt-2">Your card information is securely handled by Stripe.</div>
                    <div class="mt-3">
                      <button class="btn btn-success btn-sm" :disabled="cardSaving" @click.prevent="saveCard">
                        <i v-if="cardSaving" class="fa fa-spinner fa-spin mr-1"></i>
                        Save card
                      </button>
                      <button class="btn btn-secondary btn-sm ml-2" :disabled="cardSaving" @click.prevent="cancelAddCard">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Editable form -->
              <form @submit.prevent="save">
                <div class="form-group">
                  <label>Profile Type</label>
                  <div class="d-flex gap-3">
                    <div class="form-check form-check-inline">
                      <input class="form-check-input" type="radio" id="typeOrganization" v-model="model.type" value="BUSINESS">
                      <label class="form-check-label" for="typeOrganization">Organization</label>
                    </div>
                    <div class="form-check form-check-inline">
                      <input class="form-check-input" type="radio" id="typeIndividual" v-model="model.type" value="INDIVIDUAL">
                      <label class="form-check-label" for="typeIndividual">Individual</label>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>ID</label>
                      <input type="text" class="form-control" :value="model.id" disabled>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <template v-if="isIndividual">
                      <div class="form-group">
                        <label>ID Document Type</label>
                        <select v-model="model.personalIdType" class="form-control" :class="{ 'is-invalid': fieldError('personalIdType') }">
                          <option value="DRIVERS_LICENSE">Driver License</option>
                          <option value="GOVERNMENT_ID">Government ID</option>
                          <option value="PASSPORT">Passport</option>
                        </select>
                        <div v-if="fieldError('personalIdType')" class="invalid-feedback d-block">{{ fieldError('personalIdType') }}</div>
                      </div>
                      <div class="form-group">
                        <label>Personal ID (Driver License, Government ID or Passport)</label>
                        <input v-model="model.taxId" type="text" class="form-control" :class="{ 'is-invalid': fieldError('taxId') }" placeholder="ID number">
                        <div v-if="fieldError('taxId')" class="invalid-feedback d-block">{{ fieldError('taxId') }}</div>
                      </div>
                    </template>
                    <template v-else>
                      <div class="form-group">
                        <label>Tax / VAT ID</label>
                        <input v-model="model.taxId" type="text" class="form-control" :class="{ 'is-invalid': fieldError('taxId') }" placeholder="Tax ID">
                        <div v-if="fieldError('taxId')" class="invalid-feedback d-block">{{ fieldError('taxId') }}</div>
                      </div>
                    </template>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Name</label>
                      <input v-model="model.name" type="text" class="form-control" :class="{ 'is-invalid': fieldError('name') }" placeholder="Name">
                      <div v-if="fieldError('name')" class="invalid-feedback d-block">{{ fieldError('name') }}</div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>{{ isIndividual ? 'Payee Name' : 'Company Name' }}</label>
                      <input v-model="model.companyName" type="text" class="form-control" :class="{ 'is-invalid': fieldError('companyName') }" :placeholder="isIndividual ? 'Full name' : 'Company Name'">
                      <div v-if="fieldError('companyName')" class="invalid-feedback d-block">{{ fieldError('companyName') }}</div>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Email</label>
                      <input v-model="model.email" type="email" class="form-control" :class="{ 'is-invalid': fieldError('email') }" placeholder="Email">
                      <div v-if="fieldError('email')" class="invalid-feedback d-block">{{ fieldError('email') }}</div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Phone</label>
                      <vue-tel-input v-model="model.phone" mode="international" :class="{ 'is-invalid': fieldError('phone') }"></vue-tel-input>
                      <div v-if="fieldError('phone')" class="invalid-feedback d-block">{{ fieldError('phone') }}</div>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label>Address Line 1</label>
                  <input v-model="model.addressLine1" type="text" class="form-control" :class="{ 'is-invalid': fieldError('addressLine1') }" placeholder="Address Line 1">
                  <div v-if="fieldError('addressLine1')" class="invalid-feedback d-block">{{ fieldError('addressLine1') }}</div>
                </div>
                <div class="form-group">
                  <label>Address Line 2</label>
                  <input v-model="model.addressLine2" type="text" class="form-control" :class="{ 'is-invalid': fieldError('addressLine2') }" placeholder="Address Line 2">
                  <div v-if="fieldError('addressLine2')" class="invalid-feedback d-block">{{ fieldError('addressLine2') }}</div>
                </div>

                <div class="row">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>City</label>
                      <input v-model="model.city" type="text" class="form-control" :class="{ 'is-invalid': fieldError('city') }" placeholder="City">
                      <div v-if="fieldError('city')" class="invalid-feedback d-block">{{ fieldError('city') }}</div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>State</label>
                      <input v-model="model.state" type="text" class="form-control" :class="{ 'is-invalid': fieldError('state') }" placeholder="State">
                      <div v-if="fieldError('state')" class="invalid-feedback d-block">{{ fieldError('state') }}</div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>Postal Code</label>
                      <input v-model="model.postalCode" type="text" class="form-control" :class="{ 'is-invalid': fieldError('postalCode') }" placeholder="Postal Code">
                      <div v-if="fieldError('postalCode')" class="invalid-feedback d-block">{{ fieldError('postalCode') }}</div>
                    </div>
                  </div>
                </div>

                <div class="d-flex align-items-center">
                  <button class="btn btn-success" :disabled="saving">
                    <i v-if="saving" class="fa fa-spinner fa-spin mr-1"></i>
                    <i v-else class="fa fa-save mr-1"></i>
                    {{ $t('save') || 'Save' }}
                  </button>
                  <div v-if="saveMessage" class="text-success ml-3">{{ saveMessage }}</div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </ntm-block>

    </page-content>
  </div>
</template>

<script>
import BillingService from '@/services/billing.service'
import BillingUsageStats from '@/components/billing/BillingUsageStats.vue'
import { CARD_PAYMENTS_ENABLED } from '@/config/billing'

export default {
  name: 'BillingShow',
  components: { BillingUsageStats },
  data () {
    return {
      // the usage tab is mounted the first time it is opened
      usageOpened: this.$route.query.tab === 'usage',
      loading: true,
      saving: false,
      error: null,
      saveMessage: '',
      model: {},
      validationErrors: {},
      cardPaymentsEnabled: CARD_PAYMENTS_ENABLED,
      // Stripe card states
      clientSecret: null,
      addingCard: false,
      cardSaving: false,
      cardError: null,
      stripeInitError: null,
      stripePk: process.env.VUE_APP_STRIPE_PK || null,
      stripe: null,
      stripeElements: null,
      stripeCard: null
    }
  },
  computed: {
    activeTab () {
      return this.$route.query.tab === 'usage' ? 'usage' : 'profile'
    },
    isIndividual () {
      return this.model.type === 'INDIVIDUAL'
    },
    formattedPayment () {
      const t = this.model.pmType
      const last = this.model.pmLastFour
      if (t && last) return `${String(t).toUpperCase()} •••• ${last}`
      if (t) return String(t).toUpperCase()
      if (last) return `•••• ${last}`
      return ''
    }
  },
  watch: {
    activeTab (tab) {
      if (tab === 'usage') this.usageOpened = true
    }
  },
  created () {
    this.fetch()
  },
  beforeDestroy () {
    this.destroyCardElement()
  },
  methods: {
    setTab (tab) {
      if (tab === 'usage') this.usageOpened = true
      if (tab === this.activeTab) return
      const query = { ...this.$route.query }
      if (tab === 'usage') query.tab = 'usage'
      else delete query.tab
      this.$router.replace({ query }).catch(() => {})
    },
    fieldError (name) {
      const v = this.validationErrors && this.validationErrors[name]
      if (!v) return null
      if (Array.isArray(v)) return v[0]
      if (typeof v === 'string') return v
      return String(v)
    },
    clearValidationErrors () {
      this.validationErrors = {}
    },
    submit () {
      // this will trigger the process
      this.$refs.elementRef.submit()
    },
    tokenCreated (token) {
      console.log(token)
      // handle the token
      // send it to your server
    },
    fetch () {
      this.loading = true
      this.error = null
      const id = this.$route.params.id
      BillingService.show(id)
        .then(res => {
          const data = res?.data?.data || res?.data || {}
          this.model = { ...data }
        })
        .catch(err => {
          this.error = err?.response?.data?.message || err?.message || 'Failed to load billing profile.'
        })
        .finally(() => {
          this.loading = false
        })
    },
    save () {
      // minimal client-side email check
      this.clearValidationErrors()
      if (this.model.email && !/.+@.+\..+/.test(this.model.email)) {
        this.validationErrors = { ...this.validationErrors, email: 'Please enter a valid email address.' }
        return
      }
      this.saving = true
      this.error = null
      this.saveMessage = ''
      const id = this.$route.params.id
      const payload = {
        name: this.model.name || null,
        companyName: this.model.companyName || null,
        taxId: this.model.taxId || null,
        email: this.model.email || null,
        phone: this.model.phone || null,
        addressLine1: this.model.addressLine1 || null,
        addressLine2: this.model.addressLine2 || null,
        city: this.model.city || null,
        state: this.model.state || null,
        postalCode: this.model.postalCode || null,
        type: this.model.type || 'BUSINESS',
        personalIdType: this.model.personalIdType || null
      }
      BillingService.update(id, payload)
        .then(() => {
          this.saveMessage = 'Saved successfully.'
          this.clearValidationErrors()
        })
        .catch(err => {
          const status = err?.response?.status
          const errors = err?.response?.data?.errors
          if (status === 422 && errors) {
            this.validationErrors = errors
            // do not set this.error so the form stays visible
          } else if (errors) {
            const entries = Object.entries(errors)
            this.error = entries.map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
          } else {
            this.error = err?.response?.data?.message || err?.message || 'Failed to save billing profile.'
          }
        })
        .finally(() => {
          this.saving = false
        })
    },
    async startAddCard () {
      BillingService.createSetupIntent(this.$route.params.id).then(async (res) => {
        console.log(res)
        this.clientSecret = res.data.clientSecret
        this.stripePk = res.data.publishableKey
        this.cardError = null
        this.stripeInitError = null
        if (!this.stripePk) {
          this.stripeInitError = 'Stripe publishable key is not configured.'
          return
        }
        try {
          await this.initStripe()
          this.addingCard = true
          this.$nextTick(() => {
            try {
              this.mountCardElement()
            } catch (e) {
              this.cardError = e?.message || 'Failed to initialize card input.'
            }
          })
        } catch (e) {
          this.stripeInitError = e?.message || 'Failed to load Stripe.'
        }
      })
    },
    loadStripeJs () {
      return new Promise((resolve, reject) => {
        if (window.Stripe) return resolve()
        const existing = document.querySelector('script[src="https://js.stripe.com/v3"]')
        if (existing) {
          existing.addEventListener('load', () => resolve())
          existing.addEventListener('error', () => reject(new Error('Stripe.js failed to load')))
          return
        }
        const script = document.createElement('script')
        script.src = 'https://js.stripe.com/v3'
        script.async = true
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Stripe.js failed to load'))
        document.head.appendChild(script)
      })
    },
    async initStripe () {
      if (this.stripe && this.stripeElements) return
      await this.loadStripeJs()
      if (!window.Stripe) throw new Error('Stripe.js not available')
      this.stripe = window.Stripe(this.stripePk)
      this.stripeElements = this.stripe.elements()
    },
    mountCardElement () {
      if (!this.stripeElements) throw new Error('Stripe not initialized')
      if (this.stripeCard) return
      this.stripeCard = this.stripeElements.create('card', { hidePostalCode: true })
      const el = this.$refs.cardElement
      if (!el) throw new Error('Mount point not found')
      this.stripeCard.mount(el)
    },
    destroyCardElement () {
      try {
        if (this.stripeCard) {
          this.stripeCard.unmount()
        }
      } catch (e) { /* ignore */ }
      this.stripeCard = null
      this.stripeElements = null
    },
    cancelAddCard () {
      this.cardError = null
      this.addingCard = false
      this.destroyCardElement()
    },
    async saveCard () {
      this.cardSaving = true
      this.cardError = null
      try {
        const id = this.$route.params.id
        const clientSecret = this.clientSecret
        if (!clientSecret) throw new Error('Could not create setup intent')
        const result = await this.stripe.confirmCardSetup(clientSecret, {
          payment_method: { card: this.stripeCard }
        })
        if (result.error) {
          throw new Error(result.error.message || 'Card confirmation failed')
        }
        const pmId = result?.setupIntent?.payment_method
        if (!pmId) throw new Error('Payment method not returned by Stripe')
        await BillingService.setDefaultPaymentMethod(id, pmId)
        this.saveMessage = 'Card saved successfully.'
        this.addingCard = false
        this.destroyCardElement()
        await this.fetch()
      } catch (e) {
        this.cardError = e?.response?.data?.message || e?.message || 'Failed to save card.'
      } finally {
        this.cardSaving = false
      }
    }
  }
}
</script>
